import React, { useCallback, useEffect, useState } from "react";
import { ISelectOptions } from "../../../types/inputs";
import { Delivery } from "../../../services/Delivery";
import { LocalAtendimentoEnum } from "../../../enums/localAtendimento";
import { Agendamento } from "../../../services/Agendamento";
import { toast } from "react-toastify";
import {
  IAgendamentoCadastroForm,
  IAgendamentoDTO,
  IAtendimentoConcessionariaForm,
  IReagendamentoForm,
} from "../../../types/agendamento";
import { useContextSite } from "../../../context/Context";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSessionStorage } from "../../../hooks/useSessionStorage";
import { addDays } from "date-fns";
import { Cliente } from "../../../services/Cliente";
import { IClienteDTO } from "../../../types/cliente";
import { TipoClienteEnum } from "../../../enums/tipoCliente";
import { formatDateToISO } from "../../../utils/dateTransform";

interface FormDeliveryProps extends IAtendimentoConcessionariaForm {
  uuidDelivery: string;
  local: string;
}

interface RouteParams extends Record<string, string> {
  uuidAgendamento: string;
}

export const useDelivery = () => {
  const navigate = useNavigate();
  const [token] = useSessionStorage("@token");
  const { uuidAgendamento } = useParams<RouteParams>();
  const [searchParams, setSearchParams] = useSearchParams();
  const reagendamento = searchParams.get("reagendamento") === "true";
  const [usuario] = useSessionStorage("@usuarioMapa");
  const [cliente, setCliente] = useState<IClienteDTO>({} as IClienteDTO);
  const [cidadesOptions, setCidadesOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const localOptions = Object.entries(LocalAtendimentoEnum).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const [form, setForm] = useState<FormDeliveryProps>({} as FormDeliveryProps);
  const { setIsLoad } = useContextSite();
  const [horariosOptions, setHorariosOptions] = useState<ISelectOptions[]>([]);

  const [date, setDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [diasIndisponiveis, setDiasIndisponiveis] = useState<Date[]>([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [agendamento, setAgendamento] = useState<IAgendamentoDTO>(
    {} as IAgendamentoDTO
  );
  const [reagendamentoForm, setReagendamentoForm] =
    useState<IReagendamentoForm>({} as IReagendamentoForm);

  function handleReagendamento() {
    setIsLoad(true);
    setModalIsOpen(false);

    Agendamento.reagendar({ uuid: uuidAgendamento, props: reagendamentoForm })
      .then(() => {
        toast.success("Reagendamento efetuado com sucesso!");
        setTimeout(() => {
          navigate(`/meus-agendamentos/agendamento?id=${uuidAgendamento}`);
        }, 2000);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  const getCidades = useCallback(() => {
    Delivery.get().then(({ data }) => {
      const options = data.content.map((item) => ({
        label: item.cidade,
        value: item.uuid,
        element: item,
      }));

      setCidadesOptions(options);
    });
  }, []);

  useEffect(() => {
    getCidades();
    if (reagendamento) {
      setIsLoad(true);
      Agendamento.getById({ uuid: uuidAgendamento })
        .then(({ data }) => {
          setReagendamentoForm({
            ...reagendamentoForm,
            uuidDelivery: data?.delivery?.uuid,
            concessionaria: data?.concessionaria,
            diaAgendado: data?.diaAgendado,
            horaAgendada: data?.horaAgendada,
          });
        })
        .catch(
          ({
            response: {
              data: { mensagem },
            },
          }) => toast.error(mensagem)
        )
        .finally(() => setIsLoad(false));
    }
  }, []);

  useEffect(() => {
    Cliente.getByUsuario({
      uuidUsuario: usuario?.uuidUsuario,
    })
      .then(({ data }) => setCliente(data))
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      );
  }, [usuario]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      nome: "",
      telefone: "",
      uuidConcessionaria: "",
    }));
  }, [form?.local]);

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();

    if (form)
      if (reagendamento) {
        setModalIsOpen(true);
        return;
      }

    const PAYLOAD: IAgendamentoCadastroForm = {
      concessionaria: !!(form.local === "CONCESSIONARIA"),
      uuidDelivery: form.uuidDelivery,
    };

    const agendamentoData: IAgendamentoDTO = await Agendamento.postV2(
      PAYLOAD
    ).then(({ data }) => data);

    setAgendamento(agendamentoData);

    if (form?.local === "CONCESSIONARIA") {
      setModalConfirm(true);
      return;
    }

    if (token && cliente) {
      Agendamento.vincularAgendamentoAoCliente({
        uuid: agendamentoData?.uuid,
        uuidCliente: cliente?.uuid,
      })
        .then(() => {
          navigate(
            usuario?.tipo === TipoClienteEnum.DESPACHANTE
              ? `/agendamento/${agendamentoData?.uuid}/despachante-cliente`
              : `/agendamento/${agendamentoData?.uuid}/servicos`
          );
          return;
        })
        .catch((error) => toast.error(error.message))
        .finally(() => setIsLoad(false));
    } else {
      navigate(`/agendamento/${agendamentoData?.uuid}/login-cadastro`);
    }

    setIsLoad(false);
  }

  function handleAgendamento(agendamento: IAgendamentoDTO) {
    setIsLoad(true);

    Agendamento.vincularAgendamentoAoCliente({
      uuid: agendamento?.uuid,
      uuidCliente: cliente?.uuid,
    })
      .then(() => {
        navigate(`/agendamento/${agendamento?.uuid}/servicos`);
        return;
      })
      .catch((error) => toast.error(error.message))
      .finally(() => {
        setIsLoad(false);
        setModalConfirm(false);
      });
  }

  useEffect(() => {
    setDate(null);

    if (reagendamentoForm?.uuidDelivery) {
      setIsLoading(true);
      Delivery.getDiasIndisponiveis({
        uuidDelivery: reagendamentoForm.uuidDelivery,
        concessionaria: agendamento?.concessionaria,
      })
        .then(({ data }) => {
          const options = data.map((item) => addDays(new Date(item), 1));
          setDiasIndisponiveis(options);
        })
        .catch(
          ({
            response: {
              data: { mensagem },
            },
          }) => toast.error(mensagem)
        )
        .finally(() => setIsLoading(false));
    }
  }, [reagendamentoForm?.uuidDelivery]);

  useEffect(() => {
    setReagendamentoForm((prev) => ({ ...prev, horaAgendada: null }));
    if (date) {
      const newDate = formatDateToISO(date);

      if (reagendamentoForm?.uuidDelivery) {
        Delivery.getHorariosDisponiveis({
          uuidDelivery: reagendamentoForm?.uuidDelivery,
          dataAgendamento: newDate,
        }).then(({ data }) => {
          const options = data.map((item) => ({
            value: item,
            label: item,
            element: item,
          }));

          setHorariosOptions(options);
        });
      }
    }
  }, [date]);

  const localAtendimentoSelecionado =
    reagendamentoForm?.concessionaria === undefined
      ? undefined
      : reagendamentoForm.concessionaria
      ? "CONCESSIONARIA"
      : "DOMICILIO";

  return {
    localAtendimentoSelecionado,
    cidadesOptions,
    localOptions,
    form,
    setForm,
    reagendamentoForm,
    setReagendamentoForm,
    handleSubmit,
    isLoading,
    modalIsOpen,
    setModalIsOpen,
    diasIndisponiveis,
    horariosOptions,
    handleReagendamento,
    date,
    setDate,
    reagendamento,
    modalConfirm,
    setModalConfirm,
    handleAgendamento,
    agendamento,
  };
};
